import React, { useMemo, useState } from 'react';

const formatAllCredentials = credentials => credentials
  .map(credential => (
    `${credential.department_name || `${credential.department_number}. Wachabteilung`}\n` +
    `Mitarbeiter: ${credential.employee_password}\n` +
    `Admin: ${credential.admin_password}`
  ))
  .join('\n\n');

const writeClipboard = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) {
    throw new Error('Kopieren wird von diesem Browser nicht unterstützt.');
  }
};

const InitialCredentialsModal = ({ credentials, onConfirm }) => {
  const [copyStatus, setCopyStatus] = useState('');
  const allCredentials = useMemo(
    () => formatAllCredentials(credentials),
    [credentials]
  );

  const copy = async (text, successMessage) => {
    try {
      await writeClipboard(text);
      setCopyStatus(successMessage);
    } catch (error) {
      setCopyStatus(error.message || 'Kopieren fehlgeschlagen.');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="initial-credentials-title"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="border-b border-slate-200 px-6 py-5">
          <h2 id="initial-credentials-title" className="text-2xl font-bold text-slate-900">
            Einmalige Startzugänge
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Jetzt sicher speichern. Nach dem Schließen werden diese Passwörter nicht erneut angezeigt.
          </p>
        </header>

        <div className="space-y-4 overflow-y-auto px-6 py-5">
          {credentials.map(credential => (
            <article
              key={credential.department_number}
              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <h3 className="mb-3 font-semibold text-slate-900">
                {credential.department_name || `${credential.department_number}. Wachabteilung`}
              </h3>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg bg-white p-3 ring-1 ring-slate-200">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Mitarbeiter
                  </span>
                  <code className="mt-1 block break-all text-sm font-semibold text-slate-900">
                    {credential.employee_password}
                  </code>
                  <button
                    type="button"
                    onClick={() => copy(
                      credential.employee_password,
                      `Mitarbeiter-Passwort der ${credential.department_name || `${credential.department_number}. Wachabteilung`} kopiert.`,
                    )}
                    className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-900"
                  >
                    Mitarbeiter {credential.department_number} kopieren
                  </button>
                </div>

                <div className="rounded-lg bg-white p-3 ring-1 ring-slate-200">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Admin
                  </span>
                  <code className="mt-1 block break-all text-sm font-semibold text-slate-900">
                    {credential.admin_password}
                  </code>
                  <button
                    type="button"
                    onClick={() => copy(
                      credential.admin_password,
                      `Admin-Passwort der ${credential.department_name || `${credential.department_number}. Wachabteilung`} kopiert.`,
                    )}
                    className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-900"
                  >
                    Admin {credential.department_number} kopieren
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <footer className="border-t border-slate-200 bg-white px-6 py-4">
          {copyStatus && (
            <p role="status" className="mb-3 text-sm text-slate-600">
              {copyStatus}
            </p>
          )}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => copy(allCredentials, 'Alle Zugangsdaten wurden kopiert.')}
              className="rounded-lg border border-slate-300 px-4 py-2.5 font-semibold text-slate-700 hover:bg-slate-50"
            >
              Alle Zugangsdaten kopieren
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-lg bg-blue-600 px-4 py-2.5 font-semibold text-white hover:bg-blue-700"
            >
              Zugangsdaten sicher gespeichert
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default InitialCredentialsModal;
